// Tạo / sửa vai trò nhân viên: tên tự đặt + tick quyền (docs/KE_HOACH_THANH_VIEN_DON_VI.md). Quyền kéo theo
// (lên đơn → xem đơn, sản phẩm, khách…) tự tick và khoá; máy chủ chuẩn hoá lại khi lưu.
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { post, put } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  UNIT_PERMISSION_GROUPS,
  UNIT_PERMISSION_META,
  UNIT_ROLE_NAME_MAX,
  UNIT_ROLE_TEMPLATES,
  impliedBy,
  isUnitPermission,
  normalizeUnitPermissions,
  unitRoleTemplatePermissions,
  type UnitPermission,
  type UnitRoleTemplate,
} from '@shoptrack/common/unit-permissions'
import type { UnitRoleRow } from './types'

export function RoleDialog({ open, onOpenChange, role, tenantType, grantable }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = tạo mới */
  role: UnitRoleRow | null
  tenantType: string
  grantable: string[]
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  // Quyền người dùng tự tick (chưa gồm quyền kéo theo)
  const [picked, setPicked] = useState<Set<UnitPermission>>(new Set())
  const [error, setError] = useState<string | null>(null)

  // Đặt lại khi mở / đổi vai trò đang sửa — KHÔNG theo object `role` (tải lại ngầm khi quay lại tab sẽ xoá phần đang sửa)
  useEffect(() => {
    if (!open) return
    setName(role?.name ?? '')
    setPicked(new Set((role?.permissions ?? []).filter(isUnitPermission)))
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role?.id])

  const effective = useMemo(() => new Set(normalizeUnitPermissions([...picked], tenantType)), [picked, tenantType])
  /** Quyền đang tick chỉ vì một quyền khác kéo theo */
  const lockedBy = (p: UnitPermission): UnitPermission | null => {
    for (const q of picked) if (q !== p && impliedBy(q).includes(p)) return q
    return null
  }

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), permissions: [...effective] }
      return role ? put(`/settings/members/roles/${role.id}`, body) : post('/settings/members/roles', body)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['unit-members'] })
      toast.success(role ? 'Đã lưu vai trò' : 'Đã tạo vai trò')
      onOpenChange(false)
    },
    onError: (e) => setError((e as Error).message),
  })

  function toggle(p: UnitPermission) {
    setError(null)
    setPicked((cur) => {
      const next = new Set(cur)
      if (next.has(p)) next.delete(p)
      else next.add(p)
      return next
    })
  }

  function applyTemplate(t: UnitRoleTemplate) {
    setPicked(new Set(unitRoleTemplatePermissions(t, tenantType)))
    if (!name.trim()) setName(UNIT_ROLE_TEMPLATES[t].label)
    setError(null)
  }

  function submit() {
    if (!name.trim()) return setError('Nhập tên vai trò')
    if (!effective.size) return setError('Chọn ít nhất một quyền')
    save.mutate()
  }

  const groups = UNIT_PERMISSION_GROUPS.map((g) => ({ ...g, permissions: g.permissions.filter((p) => grantable.includes(p)) })).filter((g) => g.permissions.length)

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!save.isPending) onOpenChange(v) }}>
      <DialogContent className="sm:max-w-2xl max-h-[92dvh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{role ? 'Sửa vai trò' : 'Tạo vai trò'}</DialogTitle>
        </DialogHeader>

        <label className="block space-y-1.5">
          <span className="text-sm text-inkSoft">Tên vai trò *</span>
          <Input value={name} maxLength={UNIT_ROLE_NAME_MAX} onChange={(e) => { setName(e.target.value); setError(null) }} placeholder="VD: Lên đơn, Kho, Kế toán…" className="h-11 desktop:h-9" disabled={save.isPending} />
        </label>

        {!role && (
          <div className="space-y-1.5">
            <p className="text-sm text-inkSoft">Chọn nhanh theo mẫu</p>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(UNIT_ROLE_TEMPLATES) as UnitRoleTemplate[]).map((t) => (
                <Button key={t} type="button" size="sm" variant="outline" className="h-10 desktop:h-8" onClick={() => applyTemplate(t)} disabled={save.isPending}>
                  {UNIT_ROLE_TEMPLATES[t].label}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {groups.map((g) => (
            <fieldset key={g.key} className="space-y-1">
              <legend className="text-sm font-medium text-ink">{g.label}</legend>
              <div className="divide-y divide-rule rounded-lg border border-rule">
                {g.permissions.map((p) => {
                  const on = effective.has(p)
                  const by = on ? lockedBy(p) : null
                  const locked = by !== null && !picked.has(p)
                  return (
                    <label key={p} className={cn('flex min-h-11 cursor-pointer items-start gap-3 px-3 py-2.5', locked && 'cursor-default')}>
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 shrink-0 cursor-pointer accent-accent disabled:cursor-default"
                        checked={on}
                        disabled={locked || save.isPending}
                        onChange={() => toggle(p)}
                        aria-describedby={locked && by ? `perm-hint-${p}` : undefined}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm text-ink">{UNIT_PERMISSION_META[p].label}</span>
                        {locked && by && (
                          <span id={`perm-hint-${p}`} className="block text-xs text-muted">
                            Kèm theo "{UNIT_PERMISSION_META[by].label}"
                          </span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
          ))}
        </div>

        {error && <p role="alert" className="text-sm font-medium text-danger">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>Hủy</Button>
          <Button type="button" onClick={submit} disabled={save.isPending}>{save.isPending ? 'Đang lưu…' : role ? 'Lưu' : 'Tạo vai trò'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
